import React, { useMemo } from 'react';
import { ScrollView, View } from 'react-native';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import PointOfSalePlanogramGridCell from './PointOfSalePlanogramGridCell';

// Machine grid view: one row per planogram row, cells stretched to the widest row.
const PointOfSalePlanogramGrid = ({
	rowsWithItems,
	width,
	tenant,
	healthComplianceLabel,
}) => {
	const styles = useThemedStyles(createStyles);

	const compactScreen = width < 390;
	const columnsPerViewport = width >= 1200 ? 9
		: width >= 980 ? 8
			: width >= 840 ? 7
				: width >= 700 ? 6
					: width >= 560 ? 5
						: 4;
	const availableWidth = Math.max(width - 24, 220);
	const targetGridWidth = Math.max(180, Math.floor(availableWidth * 0.5));
	// Cells fit 12 pt labels (the smallest type size); wide machines scroll sideways as before.
	const baseCellWidth = Math.max(76, Math.min(96, Math.floor(targetGridWidth / columnsPerViewport)));
	const cellHeight = compactScreen ? 100 : 104;
	const gridCanvasWidth = useMemo(() => {
		if (!rowsWithItems.length) {
			return targetGridWidth;
		}

		const widestRowWidth = rowsWithItems.reduce((widestWidth, rowData) => {
			const rowGapTotal = Math.max(0, rowData.items.length - 1) * 4;
			const rowWidth = (rowData.items.length * baseCellWidth) + rowGapTotal;
			return Math.max(widestWidth, rowWidth);
		}, 0);

		return Math.max(targetGridWidth, widestRowWidth);
	}, [baseCellWidth, rowsWithItems, targetGridWidth]);

	const resolveGridCellWidth = (rowItemCount) => {
		if (!rowItemCount) {
			return baseCellWidth;
		}

		const rowGapTotal = Math.max(0, rowItemCount - 1) * 4;
		const expandedCellWidth = Math.floor((gridCanvasWidth - rowGapTotal) / rowItemCount);
		return Math.max(baseCellWidth, expandedCellWidth);
	};

	const resolveGridRowSizing = (rowItemCount) => {
		const rowCellWidth = resolveGridCellWidth(rowItemCount);
		const rowGapTotal = Math.max(0, rowItemCount - 1) * 4;
		const rowUsedWidth = (rowCellWidth * rowItemCount) + rowGapTotal;
		const rowRemainderWidth = Math.max(0, gridCanvasWidth - rowUsedWidth);

		return {
			rowCellWidth,
			lastCellWidth: rowCellWidth + rowRemainderWidth,
		};
	};

	return (
		<ScrollView
			horizontal={true}
			showsHorizontalScrollIndicator={gridCanvasWidth > availableWidth}
			contentContainerStyle={styles.gridScrollerContent}
		>
			<View>
				{
					rowsWithItems.map(rowData => {
						const { rowCellWidth, lastCellWidth } = resolveGridRowSizing(rowData.items.length);
						return (
							<View key={`row-${rowData.row}`} style={[styles.gridRow, { width: gridCanvasWidth }]}>
								{
									rowData.items.map((item, itemIndex) => {
										const isLastInRow = itemIndex === rowData.items.length - 1;
										return (
											<PointOfSalePlanogramGridCell
												key={item.id}
												item={item}
												gridCellWidth={isLastInRow ? lastCellWidth : rowCellWidth}
												isLastInRow={isLastInRow}
												cellHeight={cellHeight}
												tenant={tenant}
												healthComplianceLabel={healthComplianceLabel}
											/>
										);
									})
								}
							</View>
						);
					})
				}
			</View>
		</ScrollView>
	);
};

const createStyles = ({ ui: { space } }) => ({
	gridScrollerContent: {
		paddingLeft: space.gutter,
		paddingRight: space.gutter,
		paddingBottom: space.sm,
	},
	gridRow: {
		flexDirection: 'row',
		marginBottom: space.xs,
	},
});

export default PointOfSalePlanogramGrid;
